import { type ProductPreOrderSettings, StockIndication } from '@spa/generated/graphql/graphql';
import type { CommonECommerceEventItem } from './eCommerceTrackingEventSchema';
import type { ListContext } from './listContextStorage';
import type { Price } from '@spa/lib/getPriceValues';
import { calculateDiscountValue } from './calculateDiscountValue';
import { computeIsProductOnSale } from '@spa/components/product-price/computeIsProductOnSale';
import { isPreOrderActive } from './isPreOrderActive';

const isValidListContext = (listContext?: ListContext): listContext is ListContext => {
  return !!listContext && listContext.originId.length > 0 && listContext.originType.length > 0;
};

// List attribution is omitted (not emptied) when the SKU has no known list context, so the
// two list fields are optional on the produced item. On-PLP callers that always know the list
// re-assert them as required for their own event item type.
export type CommonECommerceEventItemWithOptionalList = Omit<
  CommonECommerceEventItem,
  'item_list_id' | 'item_list_name'
> & {
  item_list_id?: string;
  item_list_name?: string;
};

export interface BaseTrackingItemPrices {
  readonly currentPrice: Price;
  readonly campaignPrice: Price | null;
  readonly strikePrice: Price | null;
  readonly recommendedRetailPrice: Price | null;
  readonly lowestVariantPrice?: Price | null;
}

export interface BuildBaseTrackingItemInput {
  readonly id: string;
  readonly name: string;
  readonly brandName: string | null;
  readonly categoryNames: ReadonlyArray<string>;
  readonly listContext?: ListContext;
  readonly badges: ReadonlyArray<string> | null;
  readonly stockLevelIndication: StockIndication;
  readonly preOrderSettings: ProductPreOrderSettings | null;
  readonly preOrderEnabled: boolean;
  readonly pricesIncludeTax: boolean;
  readonly country: string;
  readonly price: number;
  readonly prices: BaseTrackingItemPrices;
}

// Single source of truth for item fields shared by view/select/cart/wishlist/purchase converters.
export const buildBaseTrackingItem = ({
  id,
  name,
  brandName,
  categoryNames,
  listContext,
  badges,
  stockLevelIndication,
  preOrderSettings,
  preOrderEnabled,
  pricesIncludeTax,
  country,
  price,
  prices
}: BuildBaseTrackingItemInput): CommonECommerceEventItemWithOptionalList => {
  const discount = calculateDiscountValue({ product: prices, country });

  return {
    item_id: id,
    item_name: name,
    item_brand: brandName ?? 'unknown',
    price,
    ...(isValidListContext(listContext)
      ? { item_list_id: listContext.originId, item_list_name: listContext.originType }
      : {}),
    ...(discount ? { discount } : {}),
    ...(categoryNames[0] ? { item_category: categoryNames[0] } : {}),
    ...(categoryNames[1] ? { item_category2: categoryNames[1] } : {}),
    ...(categoryNames[2] ? { item_category3: categoryNames[2] } : {}),
    ...(categoryNames[3] ? { item_category4: categoryNames[3] } : {}),
    ...(categoryNames[4] ? { item_category5: categoryNames[4] } : {}),
    item_is_new: (badges ?? []).includes('NEW'),
    item_is_sale: computeIsProductOnSale(country, pricesIncludeTax, prices),
    item_is_out_of_stock: stockLevelIndication === StockIndication.OUT_OF_STOCK,
    item_is_preorder: isPreOrderActive(preOrderSettings, preOrderEnabled)
  };
};
